import {
  filterArrayWithTemplate,
  getLeafColumns,
  getNodeAtPath,
  getPropertyValueByPath,
  reorderArrayWithTemplate,
  setPropertyValueByPath,
  deepClone,
} from "../../../../../react-table/helpers/utils";
import {
  COLUMN_ACCESSOR_CONSIGNMENTS_TOTAL,
  COLUMN_ACCESSOR_RECEPTACLES_TOTAL,
  HEADER_CONSIGNMENTS,
  COLUMN_LEAF_EXCLUSIONS,
  PROPERTY_ORIGINAL_GROUP_TOTAL,
} from "../redux/selectors/CarditCriticalElementsLocationSelectors";

export default (reportState, columns) =>
  // eslint-disable-next-line no-use-before-define
  transformData(reportState, columns);

function transformData(reportSate, columns) {
  // eslint-disable-next-line prefer-destructuring
  let data = reportSate.data;

  // eslint-disable-next-line no-use-before-define
  data = reportSate.filtering
    ? transformDataWithFilterAction(data, columns, reportSate.filtering)
    : data;
  // eslint-disable-next-line no-use-before-define
  data = reportSate.sorting
    ? transformDataWithSortAction(data, columns, reportSate.sorting)
    : data;

  return data;
}

function transformDataWithSortAction(data, columns, sortAction) {
  // eslint-disable-next-line prefer-destructuring
  const newSorted = sortAction.newSorted;
  // eslint-disable-next-line prefer-destructuring
  const column = sortAction.column;

  const newSortColumn = newSorted[0];
  const newSortMethod = column.sortMethod;
  if (COLUMN_LEAF_EXCLUSIONS.includes(newSortColumn.id)) {
    return data;
  }

  const leafColumns = getLeafColumns(columns, COLUMN_LEAF_EXCLUSIONS);

  const transformedData = deepClone(data);
  transformedData.forEach((row) => {
    const nodeStation = getNodeAtPath(row, newSortColumn.id);

    const pivotValuesToSort = nodeStation.slice(1);
    const pivotValuesToSortWithIndices = [];
    pivotValuesToSort.forEach((value, i) =>
      pivotValuesToSortWithIndices.push({ originalIndex: i + 1, value })
    );

    if (newSortMethod) {
      pivotValuesToSortWithIndices.sort((a, b) =>
        newSortMethod.apply(null, [a.value, b.value])
      );
    } else {
      pivotValuesToSortWithIndices.sort((a, b) =>
        (a.value ? a.value : "").localeCompare(b.value ? b.value : "")
      );
    }

    const reorderingTemplate = Array.from([0]); // 0th element should always be the first
    pivotValuesToSortWithIndices.forEach((pivotValueWithIndice) =>
      reorderingTemplate.push(pivotValueWithIndice.originalIndex)
    );

    // apply this reorderingTemplate for every column in row
    // eslint-disable-next-line no-shadow
    leafColumns.forEach((column) => {
      const nodeAtPath = getNodeAtPath(row, column.accessor);

      let reorderedValues = reorderArrayWithTemplate(
        nodeAtPath,
        reorderingTemplate
      );
      if (newSortColumn.desc) {
        const first = reorderedValues.slice(0, 1);
        const rest = reorderedValues.slice(1);
        const reversedValues = [];
        reversedValues.push(...first);
        reversedValues.push(...rest.reverse());
        reorderedValues = reversedValues;
      }

      setPropertyValueByPath(row, column.accessor, reorderedValues);
    });
  });

  return transformedData;
}

function transformDataWithFilterAction(data, columns, filterAction) {
  if (!filterAction || !filterAction.appliedFilters) {
    return data;
  }

  // eslint-disable-next-line prefer-destructuring
  const appliedFilters = filterAction.appliedFilters;

  const leafColumns = getLeafColumns(columns, COLUMN_LEAF_EXCLUSIONS);
  const columnWithGrandTotalLink = leafColumns.find(
    (column) => column.accessor === COLUMN_ACCESSOR_RECEPTACLES_TOTAL
  );

  const transformedData = deepClone(data);

  // before transforming data set original group total values
  transformedData
    .filter((row) => !row.originalGroupTotal)
    .forEach((row) => {
      const originalGroupTotal = getPropertyValueByPath(
        row,
        columnWithGrandTotalLink.accessor
      )[0];
      setPropertyValueByPath(
        row,
        PROPERTY_ORIGINAL_GROUP_TOTAL,
        originalGroupTotal
      );
    });

  appliedFilters
    .filter(
      (appliedFilter) => !COLUMN_LEAF_EXCLUSIONS.includes(appliedFilter.id)
    )
    .filter((appliedFilter) => !appliedFilter.value.clearFilter)
    .filter((appliedFilter) =>
      leafColumns.some((column) => column.accessor === appliedFilter.id)
    )
    .forEach((appliedFilter) => {
      const appliedColumn = leafColumns.find(
        (column) => column.accessor === appliedFilter.id
      );
      transformedData.forEach((row) => {
        const node = getNodeAtPath(row, appliedFilter.id);

        const pivotValuesToFilter = node.slice(1);
        const pivotValuesToFilterWithIndices = [];
        pivotValuesToFilter.forEach((value, i) =>
          pivotValuesToFilterWithIndices.push({ originalIndex: i + 1, value })
        );

        const filteringTemplate = Array.from([
          {
            originalIndex: 0,
            filtered: true,
          },
        ]);

        filteringTemplate.push(
          ...pivotValuesToFilterWithIndices.map((valueWithIndex) => ({
            originalIndex: valueWithIndex.originalIndex,
            filtered: appliedColumn.filterMethod.apply(null, [
              appliedFilter,
              valueWithIndex.value,
            ]), // apply here actual filtering method on target column
          }))
        );

        // apply this filtering template for each column
        leafColumns.forEach((column) => {
          // eslint-disable-next-line no-shadow
          const node = getNodeAtPath(row, column.accessor);
          const filteredValues = filterArrayWithTemplate(
            node,
            filteringTemplate
          );
          if (column === columnWithGrandTotalLink || column.countable) {
            filteredValues[0] = 0;
            // eslint-disable-next-line no-return-assign
            filteredValues.slice(1).forEach((v) => (filteredValues[0] += v));
          }
          setPropertyValueByPath(row, column.accessor, filteredValues);
        });

        // recalculate group total and grand total percentages
        leafColumns
          .filter((column) => column.percentage)
          .forEach((column) => {
            const percentageNode = getNodeAtPath(row, column.accessor);
            const peerCountableNode = getNodeAtPath(
              row,
              column.peerCountableColumnId
            );
            const totalNode = getNodeAtPath(
              row,
              column.accessor.startsWith(HEADER_CONSIGNMENTS)
                ? COLUMN_ACCESSOR_CONSIGNMENTS_TOTAL
                : COLUMN_ACCESSOR_RECEPTACLES_TOTAL
            );

            let groupTotalPercentage =
              (peerCountableNode[0] / totalNode[0]) * 100;
            groupTotalPercentage = (
              Math.round(groupTotalPercentage * 10) / 10
            ).toFixed(1);
            percentageNode[0] = groupTotalPercentage
              ? parseFloat(groupTotalPercentage)
              : 0.0;
          });
      });
    });

  // return rows having at least one child
  return transformedData.filter((row) => {
    const nodeReceptaclesTotal = getNodeAtPath(
      row,
      COLUMN_ACCESSOR_RECEPTACLES_TOTAL
    );
    return nodeReceptaclesTotal.length > 1;
  });
}
